/**
 * Auth service – login, register, logout, getCurrentUser.
 * Uses API service and stores token, user, and subscription in sessionStorage
 * (session-only: cleared when tab/window closes). Login API returns
 * { data: { token, email, user_name, user_id, subscription } }.
 */

import { api } from './api';
import type { LoginApiResponse, LoginSubscription } from './api';

const storage = typeof window !== 'undefined' ? sessionStorage : null;
const AUTH_TOKEN_KEY = 'auth_token';
const USER_KEY = 'auth_user';
const SUBSCRIPTION_KEY = 'auth_subscription';

export interface Subscription {
  status: string;
  start_date: string;
  end_date: string;
  cases_limit: number;
  cases_used: number;
  remaining_cases: number;
}

export interface User {
  id: string;
  email: string;
  user_name?: string;
  user_id?: string;
  username?: string;
  displayName?: string;
  [key: string]: unknown;
}

export interface LoginCredentials {
  email: string;
  password: string;
  rememberMe?: boolean;
}

export interface RegisterData {
  email: string;
  first_name: string;
  last_name?: string;
  password: string;
  confirmPassword?: string;
}

export interface VerifyEmailData {
  email: string;
  otp: string;
  first_name: string;
  last_name?: string;
  password: string;
}

export interface AuthResponse {
  token: string;
  user: User;
  expiresIn?: number;
}

/**
 * Persist user and subscription in sessionStorage. Auth itself now rides an
 * HTTP-only cookie, so `token` is usually empty and only stored if present
 * (kept for backward compatibility / any non-cookie callers).
 */
export function setAuthToken(token: string, user?: User, subscription?: Subscription | LoginSubscription): void {
  if (!storage) return;
  if (token) storage.setItem(AUTH_TOKEN_KEY, token);
  if (user) storage.setItem(USER_KEY, JSON.stringify(user));
  if (subscription) storage.setItem(SUBSCRIPTION_KEY, JSON.stringify(subscription));
}

/**
 * Get stored token.
 */
export function getAuthToken(): string | null {
  if (!storage) return null;
  return storage.getItem(AUTH_TOKEN_KEY);
}

/**
 * Get stored user (from last login/register response).
 */
export function getStoredUser(): User | null {
  if (!storage) return null;
  try {
    const raw = storage.getItem(USER_KEY);
    return raw ? (JSON.parse(raw) as User) : null;
  } catch {
    return null;
  }
}

/**
 * Get stored subscription (from last login response).
 */
export function getSubscription(): Subscription | null {
  if (!storage) return null;
  try {
    const raw = storage.getItem(SUBSCRIPTION_KEY);
    return raw ? (JSON.parse(raw) as Subscription) : null;
  } catch {
    return null;
  }
}

/**
 * Get remaining cases from stored subscription.
 */
export function getRemainingCases(): number | null {
  const sub = getSubscription();
  return sub != null ? sub.remaining_cases : null;
}

/**
 * Update stored subscription's remaining_cases (e.g. after add_report returns).
 * Call this when the backend returns a new remaining_cases count so the header updates.
 */
export function updateRemainingCases(remaining_cases: number): void {
  if (!storage) return;
  const sub = getSubscription();
  if (!sub) return;
  const updated = { ...sub, remaining_cases };
  storage.setItem(SUBSCRIPTION_KEY, JSON.stringify(updated));
}

/**
 * Clear token, user, and subscription (logout).
 */
export function clearAuth(): void {
  if (!storage) return;
  storage.removeItem(AUTH_TOKEN_KEY);
  storage.removeItem(USER_KEY);
  storage.removeItem(SUBSCRIPTION_KEY);
}

/** Normalize login API response: backend may return { data: { ... } } or { token, ... } at top level */
function normalizeLoginPayload(res: unknown): {
  token: string;
  user_id: string;
  email: string;
  user_name: string;
  subscription: Subscription;
} {
  const raw = res && typeof res === 'object' && 'data' in res
    ? (res as LoginApiResponse).data
    : (res as Record<string, unknown>);
  // Auth now rides an HTTP-only cookie set by the backend, so the response no
  // longer carries a token. Validate on the user identity instead.
  const r = (raw && typeof raw === 'object' ? raw : null) as Record<string, unknown> | null;
  if (!r || (r.user_id == null && r.id == null)) {
    throw new Error('Invalid login response: missing user');
  }
  const subscription = (r.subscription && typeof r.subscription === 'object')
    ? (r.subscription as Subscription)
    : {
        status: 'active',
        start_date: '',
        end_date: '',
        cases_limit: 0,
        cases_used: 0,
        remaining_cases: 0,
      };
  return {
    token: typeof r.token === 'string' ? r.token : '',
    user_id: String(r.user_id ?? r.id ?? ''),
    email: String(r.email ?? ''),
    user_name: String(r.user_name ?? r.displayName ?? r.email ?? 'User'),
    subscription,
  };
}

/**
 * Login. On success stores token, user, and subscription; on failure throws ApiError.
 * Accepts response either as { data: { token, email, user_name, user_id, subscription } } or flat { token, ... }.
 */
export async function login(credentials: LoginCredentials): Promise<AuthResponse> {
  const res = await api.account.login({
    email: credentials.email.trim().toLowerCase(),
    password: credentials.password,
    rememberMe: credentials.rememberMe,
  });
  const data = normalizeLoginPayload(res);
  const user: User = {
    id: data.user_id,
    user_id: data.user_id,
    email: data.email,
    user_name: data.user_name,
    displayName: data.user_name,
  };
  setAuthToken(data.token, user, data.subscription);
  return {
    token: data.token,
    user,
    expiresIn: undefined,
  };
}

/**
 * Login with Google id_token. Expects backend POST /api/dxr/login/google to return the same payload as /login.
 */
export async function loginWithGoogle(idToken: string, profile?: { email?: string; name?: string }): Promise<AuthResponse> {
  const res = await api.account.googleLogin({
    id_token: idToken,
    email: profile?.email,
    name: profile?.name,
  });
  const data = normalizeLoginPayload(res);
  const user: User = {
    id: data.user_id,
    user_id: data.user_id,
    email: data.email,
    user_name: data.user_name,
    displayName: data.user_name,
  };
  setAuthToken(data.token, user, data.subscription);
  return {
    token: data.token,
    user,
    expiresIn: undefined,
  };
}

/**
 * Request registration OTP. Sends OTP to email; does not store token.
 * Uses POST /register. After this, show OTP step and call verifyEmail to complete.
 */
export async function requestRegisterOtp(data: RegisterData): Promise<void> {
  await api.account.register({
    email: data.email.trim().toLowerCase(),
    first_name: data.first_name.trim(),
    ...(data.last_name?.trim() ? { last_name: data.last_name.trim() } : {}),
    password: data.password,
  });
}

/**
 * Verify email with OTP and complete registration. On success stores token and user.
 * Uses POST /verify_email.
 */
export async function verifyEmail(data: VerifyEmailData): Promise<AuthResponse> {
  const res = await api.account.verifyEmail({
    email: data.email.trim().toLowerCase(),
    otp: data.otp.trim(),
    first_name: data.first_name.trim(),
    ...(data.last_name?.trim() ? { last_name: data.last_name.trim() } : {}),
    password: data.password,
  });
  const normalized = normalizeLoginPayload(res);
  const user: User = {
    id: normalized.user_id,
    user_id: normalized.user_id,
    email: normalized.email,
    user_name: normalized.user_name,
    displayName: normalized.user_name,
  };
  setAuthToken(normalized.token, user, normalized.subscription);
  return {
    token: normalized.token,
    user,
    expiresIn: undefined,
  };
}

/**
 * Logout: call POST /logout with Bearer token, then clear local state.
 */
export async function logout(): Promise<void> {
  try {
    await api.account.logout();
  } catch {
    // Proceed to clear local state even if API fails (e.g. token already invalid)
  } finally {
    clearAuth();
  }
}

/**
 * Fetch current user from API. Use when you have a /me or /auth/me endpoint.
 * Returns stored user if API is not configured or request fails.
 */
export async function getCurrentUser(): Promise<User | null> {
  const stored = getStoredUser();
  const baseUrl = api.getBaseUrl();
  if (!baseUrl) return stored;
  try {
    const user = await api.get<User>('/auth/me');
    if (user && storage) storage.setItem(USER_KEY, JSON.stringify(user));
    return user;
  } catch {
    return stored;
  }
}
